import { useEffect, useMemo, useRef, useState } from "react";

const MONTHS = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];
const WEEKDAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const MINUTE_STEP = 5;

const pad = (n: number) => String(n).padStart(2, "0");

/** Date → "YYYY-MM-DDTHH:mm" (yerel saat) */
function toLocalValue(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "YYYY-MM-DDTHH:mm" → Date (geçersizse null) */
function parseLocalValue(v: string): Date | null {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

interface Props {
  /** "YYYY-MM-DDTHH:mm" biçiminde yerel tarih-saat */
  value: string;
  onChange: (value: string) => void;
  /** Bu andan öncesi seçilemez. Varsayılan: şu an. */
  minDate?: Date;
  placeholder?: string;
}

export default function DateTimePicker({ value, onChange, minDate, placeholder = "Tarih ve saat seçin" }: Props) {
  const [open, setOpen] = useState(false);
  const selected = parseLocalValue(value);
  const min = useMemo(() => minDate ?? new Date(), [minDate]);
  const minDayStart = startOfDay(min);

  const [viewYear, setViewYear] = useState((selected ?? min).getFullYear());
  const [viewMonth, setViewMonth] = useState((selected ?? min).getMonth());

  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Panel açılınca seçili saat/dakikayı görünür alana kaydır
  useEffect(() => {
    if (!open) return;
    const scrollToActive = (el: HTMLDivElement | null) => {
      const active = el?.querySelector<HTMLElement>("[data-active='true']");
      if (active && el) el.scrollTop = active.offsetTop - el.clientHeight / 2 + active.clientHeight / 2;
    };
    scrollToActive(hourListRef.current);
    scrollToActive(minuteListRef.current);
  }, [open]);

  // Dışarı tıklayınca / Escape ile kapat
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Ay ızgarası — pazartesi başlangıçlı
  const cells = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const offset = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const out: (Date | null)[] = Array(offset).fill(null);
    for (let d = 1; d <= daysInMonth; d++) out.push(new Date(viewYear, viewMonth, d));
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [viewYear, viewMonth]);

  const prevDisabled = new Date(viewYear, viewMonth, 1) <= minDayStart;

  const shiftMonth = (delta: number) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  /** Seçimi min sınırın altına düşürmeden uygula */
  const commit = (d: Date) => {
    onChange(toLocalValue(d < min ? new Date(min.getTime()) : d));
  };

  const pickDay = (day: Date) => {
    const base = selected ?? min;
    const next = new Date(day.getFullYear(), day.getMonth(), day.getDate(), base.getHours(), base.getMinutes());
    commit(next);
  };

  const pickHour = (h: number) => {
    const base = selected ?? min;
    commit(new Date(base.getFullYear(), base.getMonth(), base.getDate(), h, base.getMinutes()));
  };

  const pickMinute = (m: number) => {
    const base = selected ?? min;
    commit(new Date(base.getFullYear(), base.getMonth(), base.getDate(), base.getHours(), m));
  };

  const isToday = selected ? sameDay(selected, min) : false;
  const hourDisabled = (h: number) => isToday && h < min.getHours();
  const minuteDisabled = (m: number) =>
    isToday && selected!.getHours() === min.getHours() && m < min.getMinutes();

  const display = selected
    ? `${pad(selected.getDate())} ${MONTHS[selected.getMonth()]} ${selected.getFullYear()}, ${pad(selected.getHours())}:${pad(selected.getMinutes())}`
    : "";

  const quickJump = (daysAhead: number) => {
    const d = new Date(min.getFullYear(), min.getMonth(), min.getDate() + daysAhead);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
    pickDay(d);
  };

  return (
    <div ref={wrapRef} className="dtp">
      {/* Tetikleyici */}
      <button type="button" className={`dtp__trigger${open ? " dtp__trigger--open" : ""}`} onClick={() => setOpen((o) => !o)}>
        <i className="bi bi-calendar-event"></i>
        <span className={display ? "dtp__value" : "dtp__placeholder"}>{display || placeholder}</span>
        <i className={`bi bi-chevron-${open ? "up" : "down"} dtp__caret`}></i>
      </button>

      {open && (
        <div className="dtp__panel">
          {/* Hızlı seçim */}
          <div className="dtp__quick">
            <button type="button" onClick={() => quickJump(0)}>Bugün</button>
            <button type="button" onClick={() => quickJump(1)}>Yarın</button>
            <button type="button" onClick={() => quickJump(7)}>Gelecek hafta</button>
          </div>

          {/* Ay başlığı */}
          <div className="dtp__month">
            <button type="button" onClick={() => shiftMonth(-1)} disabled={prevDisabled} aria-label="Önceki ay">
              <i className="bi bi-chevron-left"></i>
            </button>
            <span>{MONTHS[viewMonth]} {viewYear}</span>
            <button type="button" onClick={() => shiftMonth(1)} aria-label="Sonraki ay">
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>

          {/* Gün ızgarası */}
          <div className="dtp__weekdays">
            {WEEKDAYS.map((w) => <span key={w}>{w}</span>)}
          </div>
          <div className="dtp__days">
            {cells.map((d, i) => {
              if (!d) return <span key={`e${i}`} />;
              const disabled = startOfDay(d) < minDayStart;
              const isSelected = !!selected && sameDay(d, selected);
              const today = sameDay(d, min);
              return (
                <button
                  key={d.toISOString()}
                  type="button"
                  disabled={disabled}
                  onClick={() => pickDay(d)}
                  className={[
                    "dtp__day",
                    isSelected ? "dtp__day--selected" : "",
                    today && !isSelected ? "dtp__day--today" : "",
                  ].filter(Boolean).join(" ")}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          {/* Saat — 24 saat */}
          <div className="dtp__time">
            <div className="dtp__time-head">
              <i className="bi bi-clock me-1"></i>Saat
              <strong>{selected ? `${pad(selected.getHours())}:${pad(selected.getMinutes())}` : "--:--"}</strong>
            </div>
            <div className="dtp__time-cols">
              <div className="dtp__time-col" ref={hourListRef}>
                {Array.from({ length: 24 }, (_, h) => {
                  const active = !!selected && selected.getHours() === h;
                  return (
                    <button
                      key={h}
                      type="button"
                      data-active={active}
                      disabled={hourDisabled(h)}
                      onClick={() => pickHour(h)}
                      className={`dtp__time-item${active ? " dtp__time-item--active" : ""}`}
                    >
                      {pad(h)}
                    </button>
                  );
                })}
              </div>
              <span className="dtp__time-sep">:</span>
              <div className="dtp__time-col" ref={minuteListRef}>
                {Array.from({ length: 60 / MINUTE_STEP }, (_, i) => i * MINUTE_STEP).map((m) => {
                  const active = !!selected && selected.getMinutes() === m;
                  return (
                    <button
                      key={m}
                      type="button"
                      data-active={active}
                      disabled={minuteDisabled(m)}
                      onClick={() => pickMinute(m)}
                      className={`dtp__time-item${active ? " dtp__time-item--active" : ""}`}
                    >
                      {pad(m)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <button type="button" className="dtp__done" onClick={() => setOpen(false)}>
            <i className="bi bi-check-lg me-1"></i>Tamam
          </button>
        </div>
      )}
    </div>
  );
}
